import React, { useState } from 'react';
import { Alert, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { Check, Close, SwapHoriz } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';

import { IKiVorgangStatusProposal } from '../Interfaces/IKi';
import { ILegalTransactionFullObject } from '../Interfaces/ILegalTransaction';
import { customFetcher } from '../hooks/useFetch';
import { useUpdateLegalTransactionFullObject } from '../hooks/reactQuerys/LegalTransactionQueries';

interface IProps {
  proposal: IKiVorgangStatusProposal;
}

/**
 * Vorschlagskarte: den Status eines Vorgangs ändern (#1336).
 *
 * Geändert wird erst mit dem Klick — über denselben
 * `PUT /legaltransaction/fullObject` wie im Editor, aus der Sitzung des
 * Benutzers. Der Vorgang wird direkt davor frisch geladen: gespeichert wird
 * der ganze Vorgang, und ein älterer Stand überschriebe, was inzwischen
 * jemand daran geändert hat.
 */
export const KiVorgangStatusProposalCard: React.FC<IProps> = ({ proposal }) => {
  const queryClient = useQueryClient();
  const {
    updateLegalTransactionFullObject,
    isPendingUpdateLegalTransactionFullObject,
  } = useUpdateLegalTransactionFullObject();

  const [state, setState] = useState<'open' | 'done' | 'dismissed'>('open');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (state === 'dismissed') {
    return null;
  }

  const { Payload, Display } = proposal;

  if (state === 'done') {
    return (
      <Alert severity='success' icon={<SwapHoriz fontSize='inherit' />}>
        {Display.Vorgang} steht jetzt auf „{Display.Nach}".
      </Alert>
    );
  }

  const isBusy = isLoading || isPendingUpdateLegalTransactionFullObject;

  async function handleAendern() {
    setError(null);
    setIsLoading(true);

    try {
      const vorgang = await queryClient.fetchQuery<ILegalTransactionFullObject>(
        {
          queryKey: ['legaltransaction/fullObject', Payload.idLegalTransaction],
          queryFn: () =>
            customFetcher({
              endpoint: `/legaltransaction/fullObject/${Payload.idLegalTransaction}`,
            }),
          staleTime: 0,
        }
      );

      await updateLegalTransactionFullObject({
        ...vorgang,
        idLegalTransactionState: Payload.idLegalTransactionState,
      });

      await queryClient.invalidateQueries({
        queryKey: ['legaltransaction/fullObject', Payload.idLegalTransaction],
      });
      await queryClient.invalidateQueries({
        queryKey: ['legaltransaction', 'search'],
      });

      setState('done');
    } catch (exception) {
      const detail =
        exception instanceof Error && exception.message
          ? exception.message.replace(/^\d{3} [^:]+: /, '')
          : '';

      setError(
        detail !== '' ? detail : 'Der Status konnte nicht geändert werden.'
      );
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Paper variant='outlined' sx={{ p: 1.5, borderColor: 'primary.light' }}>
      <Stack spacing={1.5}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <SwapHoriz fontSize='small' color='primary' />
          <Typography variant='subtitle2'>Status ändern</Typography>
        </Stack>

        <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap>
          <Chip
            size='small'
            label={`${Display.Vorgang ?? Payload.idLegalTransaction}${
              Display.Aktenzeichen ? ` (${Display.Aktenzeichen})` : ''
            }`}
          />
          <Chip size='small' variant='outlined' label={Display.Von ?? '?'} />
          <Typography variant='body2'>→</Typography>
          <Chip size='small' color='primary' label={Display.Nach} />
        </Stack>

        {error !== null && <Alert severity='error'>{error}</Alert>}

        <Stack direction='row' spacing={1} justifyContent='flex-end'>
          <Button
            size='small'
            color='inherit'
            startIcon={<Close />}
            disabled={isBusy}
            onClick={() => setState('dismissed')}
          >
            Verwerfen
          </Button>
          <Button
            size='small'
            variant='contained'
            startIcon={<Check />}
            disabled={isBusy}
            onClick={() => void handleAendern()}
          >
            Ändern
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};
